import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Html5Qrcode } from "html5-qrcode";
import { QRCodeCanvas } from "qrcode.react";
import {
  Box, Typography, Button, Paper, CircularProgress, IconButton, Dialog,
  DialogTitle, DialogContent, DialogActions, Tabs, Tab,
} from "@mui/material";
import {
  QrCodeScanner, QrCode2, Home as HomeIcon, EventAvailable,
  Logout as LogoutIcon, Refresh as RefreshIcon, Download as DownloadIcon,
  FlashlightOn, FlashlightOff, Cameraswitch, Close,
} from "@mui/icons-material";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  GetMyTodayStatus, GetEmployeeDetails, MarkAbsent, EmployeeScan,
} from "../../../../Services/Services";

const SCANNER_ID = "emp-scanner";

function EmployeeDashboard() {
  const navigate = useNavigate();

  const empName = localStorage.getItem("emp_name") || "Employee";
  const empUniqueId = localStorage.getItem("emp_unique_id") || "";
  const empUserId = localStorage.getItem("emp_id") || "";
  const empProfileId = localStorage.getItem("emp_profile_id") || empUserId;

  const qrRef = useRef();
  const scannerRef = useRef(null);

  const [activeTab, setActiveTab] = useState(0);
  const [showScanner, setShowScanner] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanError, setScanError] = useState("");
  const [locationError, setLocationError] = useState("");
  const [location, setLocation] = useState({ latitude: null, longitude: null });
  const [todayStatus, setTodayStatus] = useState(null);
  const [myQrToken, setMyQrToken] = useState(localStorage.getItem("emp_qr_token") || "");
  const [qrLoading, setQrLoading] = useState(false);
  const [showAbsentDialog, setShowAbsentDialog] = useState(false);
  const [absentLoading, setAbsentLoading] = useState(false);
  const [time, setTime] = useState("");
  const [facingMode, setFacingMode] = useState("environment");
  const [flashOn, setFlashOn] = useState(false);

  useEffect(() => {
    if (localStorage.getItem("emp_logged_in") !== "true") {
      navigate("/employee-login", { replace: true });
      return;
    }
    window.history.pushState({ empApp: true }, "", window.location.href);
    const onPopState = () => window.history.pushState({ empApp: true }, "", window.location.href);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [navigate]);

  useEffect(() => {
    const updateTime = () => setTime(new Date().toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit", second: "2-digit" }));
    updateTime();
    const i = setInterval(updateTime, 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    loadTodayStatus();
    loadMyQR();
  }, []);

  const loadTodayStatus = async () => {
    try {
      const pid = localStorage.getItem("emp_profile_id") || localStorage.getItem("emp_id");
      if (!pid) return;
      const data = await GetMyTodayStatus(`profile_id=${pid}`);
      const records = Array.isArray(data) ? data : data?.results || [];
      if (records.length > 0) setTodayStatus(records[0]);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMyQR = async () => {
    const localToken = localStorage.getItem("emp_qr_token");
    if (localToken) { setMyQrToken(localToken); return; }
    const uid = localStorage.getItem("emp_id");
    if (!uid) return;
    setQrLoading(true);
    try {
      const data = await GetEmployeeDetails(uid);
      if (data?.qr_token) {
        setMyQrToken(data.qr_token);
        localStorage.setItem("emp_qr_token", data.qr_token);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setQrLoading(false);
    }
  };

  const downloadMyQR = () => {
    const canvas = qrRef.current?.querySelector("canvas");
    if (!canvas) return toast.error("QR not found");
    const newCanvas = document.createElement("canvas");
    const padding = 40, textHeight = 80;
    newCanvas.width = canvas.width + padding * 2;
    newCanvas.height = canvas.height + padding * 2 + textHeight;
    const ctx = newCanvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, newCanvas.width, newCanvas.height);
    ctx.fillStyle = "#1a1a1a";
    ctx.font = "bold 16px Arial";
    ctx.textAlign = "center";
    ctx.fillText(empName, newCanvas.width / 2, 26);
    ctx.drawImage(canvas, padding, padding + 10);
    ctx.font = "14px Arial";
    ctx.fillStyle = "#555555";
    ctx.fillText(empUniqueId, newCanvas.width / 2, canvas.height + padding + 45);
    const link = document.createElement("a");
    link.href = newCanvas.toDataURL("image/png");
    link.download = `MyQR_${empUniqueId}.png`;
    link.click();
    toast.success("QR downloaded!");
  };

  const getLocation = () =>
    new Promise((resolve, reject) => {
      if (!navigator.geolocation) return reject("Geolocation not supported");
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const loc = { latitude: pos.coords.latitude, longitude: pos.coords.longitude };
          setLocation(loc);
          resolve(loc);
        },
        (err) => {
          let msg = "Location denied";
          if (err.code === 1) msg = "Please allow location access";
          if (err.code === 2) msg = "Location unavailable";
          if (err.code === 3) msg = "Location timeout";
          reject(msg);
        },
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
      );
    });

  const openScanner = async () => {
    setScanResult(null); setScanError(""); setLocationError("");
    try {
      await getLocation();
      setShowScanner(true);
    } catch (err) {
      setLocationError(err);
      toast.error(err);
    }
  };

  const stopScanner = async () => {
    try {
      if (scannerRef.current && scannerRef.current.isScanning) {
        await scannerRef.current.stop();
        await scannerRef.current.clear();
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    if (!showScanner || scanResult || scanError) return;
    let cancelled = false;
    const start = async () => {
      await stopScanner();
      if (!scannerRef.current) scannerRef.current = new Html5Qrcode(SCANNER_ID);
      try {
        await scannerRef.current.start(
          { facingMode },
          { fps: 10, qrbox: { width: 250, height: 250 } },
          async (decodedText) => {
            if (cancelled) return;
            await stopScanner();
            handleScan(decodedText);
          },
          () => {}
        );
      } catch (err) {
        console.error("Camera start error:", err);
        toast.error("Camera start failed");
      }
    };
    const t = setTimeout(start, 300);
    return () => { cancelled = true; clearTimeout(t); stopScanner(); };
  }, [showScanner, facingMode, scanResult, scanError]);

  const handleScan = async (scannedData) => {
    setScanResult(null); setScanError("");
    let qrToken = scannedData.trim();
    if (qrToken.includes("token=")) {
      try {
        const url = new URL(qrToken);
        qrToken = url.searchParams.get("token") || qrToken;
      } catch {
        const match = qrToken.match(/token=([^&\s]+)/);
        if (match) qrToken = match[1];
      }
    }
    try {
      const uid = localStorage.getItem("emp_id");
      const data = await EmployeeScan({ qr_token: qrToken, user_id: uid, latitude: location.latitude, longitude: location.longitude });
      setScanResult(data);
      toast.success(data.message || "Attendance marked!");
      loadTodayStatus();
    } catch (err) {
      const msg = err.response?.data?.error || "Scan failed";
      setScanError(msg);
      toast.error(msg);
    }
  };

  const toggleCamera = () => { setFacingMode((prev) => (prev === "environment" ? "user" : "environment")); setFlashOn(false); };

  const toggleFlashlight = async () => {
    if (scannerRef.current && scannerRef.current.isScanning) {
      try {
        await scannerRef.current.applyVideoConstraints({ advanced: [{ torch: !flashOn }] });
        setFlashOn(!flashOn);
      } catch { toast.error("Flashlight not supported"); }
    }
  };

  const closeScanner = async () => { await stopScanner(); setShowScanner(false); setFlashOn(false); };
  const resetScanner = async () => { await stopScanner(); setScanResult(null); setScanError(""); setShowScanner(false); setFlashOn(false); };

  const markAbsent = async () => {
    setAbsentLoading(true);
    try {
      const empId = localStorage.getItem("emp_profile_id") || localStorage.getItem("emp_id");

      if (!empId || empId === "null" || empId === "undefined" || empId.trim() === "") {
        toast.error("Session expired. Please login again.");
        setShowAbsentDialog(false);
        navigate("/employee-login", { replace: true });
        return;
      }

      const data = await MarkAbsent({ profile_id: empId, employee_id: empId, id: empId });

      if (data?.success === true) {
        toast.success(data.message || "Marked as Absent");
        setShowAbsentDialog(false);
        loadTodayStatus();
      } else {
        toast.warning(data?.message || "Already marked");
        setShowAbsentDialog(false);
        loadTodayStatus();
      }
    } catch (err) {
      toast.error(err.response?.data?.error || err.response?.data?.message || "Failed to mark absent");
    } finally {
      setAbsentLoading(false);
    }
  };

  const handleLogout = async () => {
    if (!window.confirm("Are you sure you want to logout?")) return;
    await stopScanner();
    ["emp_logged_in", "emp_id", "emp_name", "emp_unique_id", "emp_profile_id", "emp_qr_token"].forEach((k) => localStorage.removeItem(k));
    toast.info("Logged out successfully");
    navigate("/employee-login", { replace: true });
  };

  const getTodayDate = () => new Date().toLocaleDateString("en-IN", { weekday: "long", day: "2-digit", month: "short", year: "numeric" });

  return (
    <>
      <ToastContainer />
      <Box sx={{ minHeight: "100vh", bgcolor: "#f8f9fa", p: 2 }}>

        {/* Header */}
        <Paper sx={{ p: 2, borderRadius: "12px", mb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Box>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b" }}>{empName}</Typography>
            <Typography sx={{ fontSize: "11px", color: "#64748b", fontFamily: "monospace" }}>{empUniqueId}</Typography>
            <Typography sx={{ fontSize: "10px", color: "#94a3b8", mt: 0.5 }}>{getTodayDate()}</Typography>
          </Box>
          <Box sx={{ textAlign: "right" }}>
            <Typography sx={{ fontSize: "20px", fontWeight: 700, color: "#ff8800", fontFamily: "monospace" }}>{time}</Typography>
            <IconButton onClick={handleLogout} sx={{ color: "#dc2626", p: 0.5 }} title="Logout">
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Box>
        </Paper>

        {/* Today Status */}
        {todayStatus && (
          <Paper sx={{ p: 1.5, borderRadius: "12px", mb: 2, bgcolor: todayStatus.status === "Present" ? "#f0fdf4" : "#fef2f2", borderLeft: `4px solid ${todayStatus.status === "Present" ? "#16a34a" : "#dc2626"}` }}>
            <Typography sx={{ fontSize: "13px", fontWeight: 600, color: todayStatus.status === "Present" ? "#16a34a" : "#dc2626" }}>
              Today: {todayStatus.status}
            </Typography>
            {todayStatus.in_time && (
              <Typography sx={{ fontSize: "11px", color: "#64748b", mt: 0.5 }}>
                In: {todayStatus.in_time}
                {todayStatus.out_time && ` | Out: ${todayStatus.out_time}`}
                {todayStatus.total_time && todayStatus.total_time !== "--" && ` | ${todayStatus.total_time}`}
              </Typography>
            )}
          </Paper>
        )}

        {/* Tabs */}
        <Paper sx={{ borderRadius: "12px", mb: 2 }}>
          <Tabs value={activeTab}
            onChange={async (e, val) => { await stopScanner(); setActiveTab(val); setShowScanner(false); }}
            variant="fullWidth"
            sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 600, fontSize: "13px", minHeight: 48, color: "#64748b" }, "& .Mui-selected": { color: "#ff8800 !important" }, "& .MuiTabs-indicator": { backgroundColor: "#ff8800" } }}>
            <Tab icon={<QrCodeScanner sx={{ fontSize: 20 }} />} iconPosition="start" label="Scanner" />
            <Tab icon={<QrCode2 sx={{ fontSize: 20 }} />} iconPosition="start" label="My QR" />
          </Tabs>
        </Paper>

        {/* TAB 1 - Scanner */}
        {activeTab === 0 && (
          <>
            {!showScanner && !scanResult && !scanError && !locationError && (
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Paper onClick={openScanner}
                  sx={{ p: 3, borderRadius: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 2, border: "2px solid transparent", "&:hover": { border: "2px solid #ff8800", bgcolor: "#fff8f0" } }}>
                  <Box sx={{ bgcolor: "#dcfce7", p: 1.5, borderRadius: "12px" }}>
                    <QrCodeScanner sx={{ fontSize: 32, color: "#16a34a" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>Scan Office QR</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#64748b" }}>Mark Present</Typography>
                  </Box>
                </Paper>

                <Paper onClick={() => setShowAbsentDialog(true)}
                  sx={{ p: 3, borderRadius: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 2, border: "2px solid transparent", "&:hover": { border: "2px solid #ef4444", bgcolor: "#fef2f2" } }}>
                  <Box sx={{ bgcolor: "#fee2e2", p: 1.5, borderRadius: "12px" }}>
                    <HomeIcon sx={{ fontSize: 32, color: "#dc2626" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>Mark Absent</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#64748b" }}>No scan needed</Typography>
                  </Box>
                </Paper>

                <Paper onClick={() => navigate("/employee-attendance-history")}
                  sx={{ p: 3, borderRadius: "16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 2, border: "2px solid transparent", "&:hover": { border: "2px solid #3b82f6", bgcolor: "#eff6ff" } }}>
                  <Box sx={{ bgcolor: "#dbeafe", p: 1.5, borderRadius: "12px" }}>
                    <EventAvailable sx={{ fontSize: 32, color: "#3b82f6" }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>My Attendance</Typography>
                    <Typography sx={{ fontSize: "11px", color: "#64748b" }}>View history</Typography>
                  </Box>
                </Paper>
              </Box>
            )}

            {locationError && (
              <Paper sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px", color: "#dc2626", mt: 1, mb: 2 }}>{locationError}</Typography>
                <Button onClick={openScanner} variant="contained" sx={{ bgcolor: "#ff8800", textTransform: "none" }}>Retry</Button>
              </Paper>
            )}

            {showScanner && !scanResult && !scanError && (
              <Paper sx={{ p: 2, borderRadius: "12px", textAlign: "center" }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
                  <Typography sx={{ fontSize: "15px", fontWeight: 700 }}>Scan Office QR</Typography>
                  <Button size="small" color="error" variant="outlined" onClick={closeScanner} startIcon={<Close />}>Close</Button>
                </Box>
                <Box sx={{ position: "relative", maxWidth: 320, mx: "auto" }}>
                  <Box id={SCANNER_ID} sx={{ width: "100%", borderRadius: "10px", overflow: "hidden", border: "2px solid #e2e8f0" }} />
                  <Box sx={{ position: "absolute", bottom: 12, width: "100%", display: "flex", justifyContent: "center", gap: 3 }}>
                    <IconButton onClick={toggleFlashlight}
                      sx={{ bgcolor: flashOn ? "#f59e0b" : "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                      {flashOn ? <FlashlightOff /> : <FlashlightOn />}
                    </IconButton>
                    <IconButton onClick={toggleCamera}
                      sx={{ bgcolor: "rgba(0,0,0,0.6)", color: "#fff", "&:hover": { bgcolor: "rgba(0,0,0,0.8)" } }}>
                      <Cameraswitch />
                    </IconButton>
                  </Box>
                </Box>
              </Paper>
            )}

            {scanResult && (
              <Paper sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}>
                <Typography sx={{ fontSize: "18px", fontWeight: 700, color: "#15803d", mt: 1 }}>{scanResult.message}</Typography>
                <Box sx={{ mt: 2, p: 2, bgcolor: "#f0fdf4", borderRadius: "8px" }}>
                  <Typography sx={{ fontSize: "12px", color: "#64748b" }}>
                    In: {scanResult.in_time}{scanResult.out_time && ` | Out: ${scanResult.out_time}`}
                  </Typography>
                </Box>
                <Button onClick={resetScanner} startIcon={<RefreshIcon />}
                  sx={{ mt: 2, bgcolor: "#ff8800", color: "#fff", textTransform: "none" }}>Done</Button>
              </Paper>
            )}

            {scanError && (
              <Paper sx={{ p: 3, borderRadius: "12px", textAlign: "center" }}>
                <Typography sx={{ fontSize: "14px", color: "#dc2626", fontWeight: 600, mt: 1 }}>{scanError}</Typography>
                <Button onClick={resetScanner} startIcon={<RefreshIcon />}
                  sx={{ mt: 2, bgcolor: "#dc2626", color: "#fff", textTransform: "none" }}>Try Again</Button>
              </Paper>
            )}
          </>
        )}

        {/* TAB 2 - My QR */}
        {activeTab === 1 && (
          <Paper sx={{ p: 3, borderRadius: "16px", textAlign: "center" }}>
            <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#1e293b", mb: 0.5 }}>My QR Code</Typography>
            <Typography sx={{ fontSize: "11px", color: "#64748b", mb: 2 }}>Show this QR to admin scanner</Typography>
            {qrLoading ? (
              <Box sx={{ py: 4 }}><CircularProgress sx={{ color: "#ff8800" }} /></Box>
            ) : myQrToken ? (
              <>
                <Box ref={qrRef} sx={{ display: "inline-block", p: 2, bgcolor: "#fff", borderRadius: "12px", border: "2px solid #f1f5f9", mb: 2 }}>
                  <QRCodeCanvas value={myQrToken} size={220} level="H" includeMargin={true} />
                </Box>
                <Box sx={{ p: 1.5, bgcolor: "#FCF5EB", borderRadius: "8px", mb: 2 }}>
                  <Typography sx={{ fontSize: "14px", fontWeight: 700 }}>{empName}</Typography>
                  <Typography sx={{ fontSize: "12px", color: "#64748b", fontFamily: "monospace" }}>{empUniqueId}</Typography>
                </Box>
                <Button onClick={downloadMyQR} startIcon={<DownloadIcon />}
                  sx={{ bgcolor: "#059669", color: "#fff", px: 3, py: 1, borderRadius: "8px", textTransform: "none" }}>
                  Download QR
                </Button>
              </>
            ) : (
              <Box sx={{ py: 4 }}>
                <Typography sx={{ fontSize: "14px", color: "#64748b" }}>QR not generated yet</Typography>
                <Typography sx={{ fontSize: "11px", color: "#94a3b8", mt: 0.5 }}>Contact admin to generate your QR</Typography>
              </Box>
            )}
          </Paper>
        )}

        {/* Absent Dialog */}
        <Dialog open={showAbsentDialog} onClose={() => !absentLoading && setShowAbsentDialog(false)}>
          <DialogTitle>Mark Absent</DialogTitle>
          <DialogContent>
            <Typography sx={{ fontSize: "14px" }}>Mark yourself as <strong>Absent</strong> for today?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAbsentDialog(false)} disabled={absentLoading} sx={{ textTransform: "none" }}>Cancel</Button>
            <Button onClick={markAbsent} disabled={absentLoading}
              sx={{ bgcolor: "#dc2626", color: "#fff", textTransform: "none", "&:hover": { bgcolor: "#b91c1c" } }}>
              {absentLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Yes, Mark Absent"}
            </Button>
          </DialogActions>
        </Dialog>

      </Box>
    </>
  );
}

export default EmployeeDashboard;